import React from 'react';
import { RichText, RichTextBlock } from 'prismic-reactjs';
import CallToAction from '@components/call-to-action';
import PhotographyHero from '@components/photography-hero';
import { ImageProps } from '@components/image';
import TextImage from '@components/text-and-image';
import Listing from '@components/listing';
import Showcase from '@components/showcase';
import EnclosedNewsletterForm from '@components/enclosed-newsletter';
import StatBlocks from '@components/stat-blocks';
import Tout from '@components/tout';
import CardTouts from '@components/card-touts';
import Solutions from '@components/solutions';
import TransitTypes from '@components/transit-types';
import FeatureCase from '@components/feature-case';
import Feedbacks from '@components/feedbacks';
import Wrapper from '@components/wrapper.styled';
import { RichTextContainer } from '@components/paragraphs/paragraphs.shared.styled';
import SecurityLabels from '@components/security-labels';
import ImageSlice from '@components/image-slice';
import LastPublicationDate from '@components/last-updated-date';
import PathCards from '@components/path-cards';
import TextSection, { TextAlign } from '@components/text-section';
import ImageBox from '@components/image-box';
import PartnerAdvantage from '@components/partner-advantage';
import { defaultTheme } from '@styles/theme.styled';
import Partners from '@components/partners';
import Spectrum from '@components/spectrum';
import MasterDispatch from '@components/master-dispatch';
import Graphics from '@components/graphics';
import MeetTheLeaders from '@components/meet-the-leaders';
import Mission from '@components/mission';
import Dashboard from '@components/dashboard';
import Relationships from '@components/relationships';
import Promotions from '@components/promotions';
import Quote from '@components/quote';
import ContactFormSection from '@components/contact-form-section';
import Patterns from '@components/patterns';
import Carousel from '@components/carousel';
import AppDownload from '@components/app-download';
import Section from '@components/section';
import NewsletterFormSection from '@components/newsletter-form';
import ConfirmationMessage from '@components/confirmation-message';
import ResourcePosts from '@components/resource-posts';
import Comparison, { ColumnProps, RowTitlesProps } from '@components/comparison';
import CTANewsletter from '@components/cta-newsletter';
import FeatureMap from '@components/feature-map';
import Feature from '@components/feature';
import FeatureCarousel from '@components/feature-carousel';
import LongContent from '@components/long-content';
import Video from '@components/video';
import ContentColumns from '@components/content-columns';
import FeatureLists from '@components/feature-list';
import Ticker from '@components/ticker';
import NotificationOverlay from '@components/notification-overlay';
import PageHero from '@components/page-hero';
import EmbedSlice from '@components/embed';
import TextImageCta from '@components/text-image-cta';
import { Item } from './item-props.type';

export interface PrimaryProps {
  anchor_link?: string;
  top_anchor_link?: boolean;
  eyebrow?: {
    text: string;
  };
  title?: {
    text: string;
  };
  items_per_row?: number;
  quoted_title?: boolean;
  positions_title?: {
    text: string;
  };
  description?: {
    richText: RichTextBlock[];
  };
  top_text?: {
    richText: RichTextBlock[];
  };
  right_text?: {
    richText: RichTextBlock[];
  };
  left_text?: {
    richText: RichTextBlock[];
  };
  bottom_text?: {
    richText: RichTextBlock[];
  };
  subtitle?: {
    richText: RichTextBlock[];
    text?: string;
  };
  subheading?: {
    richText: RichTextBlock[];
    text: string;
  };
  embed?: {
    html?: string;
    embed_url?: string;
    url?: string;
  };
  link?: {
    url?: string;
    uid?: string;
    target?: string;
  };
  link_label?: string;
  primary_link?: {
    url?: string;
    uid?: string;
    target?: string;
  };
  primary_link_title?: string;
  secondary_link?: {
    url?: string;
    uid?: string;
    target?: string;
  };
  secondary_link_title?: string;
  secondary_link_label?: string;
  image?: ImageProps;
  enclosed_container?: boolean;
  alignment?: boolean;
  image_alignment?: 'default' | 'left' | 'right';
  image_crop?: 'none' | 'bottom' | 'top' | 'left' | 'right';
  size?: string;
  confirmation_title?: {
    text: string;
  };
  show_overflow?: boolean;
  lists: {
    document: {
      data: {
        first_title: {
          text: string;
        };
        first_column: {
          image: ImageProps;
          description: {
            richText: RichTextBlock[];
          };
        }[];
        second_title: {
          text: string;
        };
        second_column: {
          image: ImageProps;
          description: {
            richText: RichTextBlock[];
          };
        }[];
      };
    };
  };
  emphasized_description?: boolean;
  confirmation_message: {
    richText?: RichTextBlock[];
  };
  rich_text: {
    richText?: RichTextBlock[];
  };
  quote?: {
    richText?: RichTextBlock[];
  };
  legal_text: {
    richText?: RichTextBlock[];
  };
  condensed?: boolean;
  blend_image?: boolean;
  author_name?: string | null;
  author_title?: string | null;
  items_in_row?: string;
  align?: TextAlign;
  layout_type?: string;
  video_url?: string;
  items_layout_type?: string;
  section_style?: boolean;
  form_id: string;
  image_fit?: boolean;
  embed_code?: string;
  button_label: string;
  background_alignment?: string;
  outer_background_color?: keyof typeof defaultTheme.backgroundColors;
  background_color?: keyof typeof defaultTheme.backgroundColors;
  background_image?: ImageProps;
  left_align_content?: boolean;
  display_border?: boolean;
  is_shifted?: boolean;
  condensed_section?: boolean;
  ticker?: boolean;
  twitter_icon?: ImageProps;
  linkedin_icon?: ImageProps;
  facebook_icon?: ImageProps;
  slides_to_show?: string;
  number_in_rows?: string;
  is_enclosed: boolean;
  is_thumbnail?: boolean;
  enclosure_color?: keyof typeof defaultTheme.backgroundColors;
  is_border_bottom?: boolean;
  font_size?: keyof typeof defaultTheme.fontSize;
  font_color?: 'dark' | 'white';
  portal_id: string;
  notification_message?: {
    text: string;
  };
  notification_icon?: ImageProps;
  notification_url?: {
    url?: string;
  };
  columns?: number;
  orientation?: boolean;
  is_notification_dismissible?: boolean;
  table_content: {
    document?: {
      data: {
        title: {
          text: string;
        };
        description: {
          richText: RichTextBlock[];
        };
        row_titles: RowTitlesProps[];
        body: ColumnProps[];
      };
    };
  };
}

export interface ModuleProps {
  slice_type: string;
  primary: PrimaryProps;
  items: Item[];
}

const renderModule = (
  { slice_type, primary, items }: ModuleProps,
  last_publication_date: string
): JSX.Element | null => {
  const itemsPerRow = primary?.items_in_row ? +primary.items_in_row : 2;

  switch (slice_type) {
    case 'page_hero':
      return (
        <PageHero
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          image={primary.image}
          linkLabel={primary.link_label}
          link={primary.link}
          imageFit={primary.image_fit}
          description={primary.description?.richText}
        />
      );
    case 'photo_hero':
      return (
        <PhotographyHero
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          image={primary.image}
          backgroundImage={primary.background_image}
          linkLabel={primary.link_label}
          link={primary.link}
        />
      );
    case 'app_download':
      return (
        <AppDownload
          description={primary.description?.richText}
          items={items}
          backgroundColor={primary.background_color}
        />
      );
    case 'text':
      return (
        <TextSection
          title={primary.title?.text}
          quotedTitle={primary.quoted_title}
          description={primary.description?.richText}
          image={primary.image}
          align={primary.align}
          backgroundColor={primary.background_color}
          emphasizedDescription={primary.emphasized_description}
        />
      );
    case 'showcase':
      return (
        <Showcase
          title={primary.title?.text}
          description={primary.description?.richText}
          backgroundColor={primary.background_color}
          items={items}
          link={primary.link}
          linkLabel={primary.link_label}
        />
      );
    case 'call_to_action':
      return (
        <CallToAction
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          buttonLabel={primary.link_label}
          link={primary.link?.url}
          backgroundColor={primary.background_color}
        />
      );
    case 'patterns':
      return (
        <Patterns
          title={primary.title?.text}
          items={items}
          link={primary.link}
          backgroundAlignment={primary.background_alignment}
          backgroundColor={primary.background_color}
          linkLabel={primary.link_label}
        />
      );
    case 'feature_case':
      return <FeatureCase items={items} />;
    case 'master_dispatch':
      return (
        <MasterDispatch
          image={primary.image}
          title={primary.title?.text}
          description={primary.description?.richText}
          items={items}
          orientation={primary.orientation}
          backgroundColor={primary.background_color}
          enclosureColor={primary.enclosure_color}
          showOverflow={primary.show_overflow}
          columns={primary.columns}
        />
      );
    case 'text_and_image':
      return (
        <TextImage
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          image={primary.image}
          imageFit={primary.image_fit}
          backgroundImage={primary.background_image}
          enclosureColor={primary.enclosure_color}
          backgroundColor={primary.background_color}
          condensedSection={primary.condensed_section}
          align={primary.align}
          items={items}
        />
      );
    case 'transit_types':
      return <TransitTypes generalTitle={primary.title?.text} items={items} />;
    case 'image_slice':
      return (
        <ImageSlice
          image={primary.image}
          title={primary.title?.text}
          description={primary.description?.richText}
        />
      );
    case 'listing':
      return (
        <Listing
          title={primary.title?.text}
          items={items}
          backgroundColor={primary.background_color}
          condensed={primary.condensed}
        />
      );
    case 'feedbacks':
      return (
        <Feedbacks
          title={primary.title?.text}
          backgroundColor={primary.background_color}
          items={items}
        />
      );
    case 'security_labels':
      return (
        <SecurityLabels
          title={primary.title?.text}
          description={primary.description?.richText}
          backgroundColor={primary.background_color}
          enclosureColor={primary.enclosure_color}
          itemsPerRow={itemsPerRow}
          ticker={primary.ticker}
          items={items}
        />
      );
    case 'rich_text':
      return (
        <Section backgroundColor="white">
          <Wrapper>
            <RichTextContainer>
              <RichText render={primary.rich_text?.richText || []} />
            </RichTextContainer>
          </Wrapper>
        </Section>
      );
    case 'tout':
      return (
        <Tout
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          backgroundColor={primary.background_color}
          image={primary.image}
          link={primary.link}
          linkLabel={primary.link_label}
          layoutType={primary.layout_type}
        />
      );
    case 'last_updated_date':
      return (
        <LastPublicationDate align={primary.align} lastPublicationDate={last_publication_date} />
      );
    case 'touts_in_row':
      return <PathCards title={primary.title?.text} items={items} />;
    case 'partners':
      return (
        <Partners
          title={primary.title?.text}
          description={primary.description?.richText}
          backgroundColor={primary.background_color}
          items={items}
          slidesToShow={+(primary.slides_to_show || '4')}
          numberInRows={+(primary.number_in_rows || '2')}
        />
      );
    case 'relationships':
      return (
        <Relationships
          title={primary.title?.text}
          description={primary.description?.richText}
          displayBorder={primary.display_border}
          backgroundAlignment={primary.background_alignment}
          outerbackgroundColor={primary.outer_background_color}
          backgroundColor={primary.background_color}
          isEnclosed={primary.is_enclosed}
          items={items}
        />
      );
    case 'cardtouts':
      return (
        <CardTouts
          title={primary.title?.text}
          itemsPerRow={itemsPerRow}
          subheading={primary.subheading?.richText}
          items={items}
          backgroundColor={primary.background_color}
        />
      );
    case 'partner_advantage':
      return (
        <PartnerAdvantage
          backgroundColor={primary.background_color}
          title={primary.title?.text}
          description={primary.description?.richText}
          items={items}
        />
      );
    case 'spectrum':
      return <Spectrum title={primary.title?.text} items={items} />;
    case 'graphics':
      return (
        <Graphics
          title={primary.title?.text}
          description={primary.description?.richText}
          topText={primary.top_text?.richText}
          rightText={primary.right_text?.richText}
          leftText={primary.left_text?.richText}
          bottomText={primary.bottom_text?.richText}
          image={primary.image}
        />
      );
    case 'meet_the_leaders': {
      return (
        <MeetTheLeaders items={items} title={primary.title?.text} items_per_row={itemsPerRow} />
      );
    }
    case 'imagebox':
      return (
        <ImageBox
          title={primary.title?.text}
          description={primary.description?.richText}
          image={primary.image}
          link={primary.link}
          link_label={primary.link_label}
        />
      );
    case 'dashboard':
      return (
        <Dashboard
          title={primary.title?.text}
          description={primary.description?.richText}
          backgroundColor={primary.background_color}
          itemsPerRow={itemsPerRow}
          items={items}
        />
      );
    case 'solutions':
      return (
        <Solutions
          generalTitle={primary.title?.text}
          subheading={primary.subheading?.richText}
          image={primary.image}
          items={items}
        />
      );
    case 'mission':
      return (
        <Mission
          eyebrow={primary.eyebrow?.text}
          title={primary.title?.text}
          image={primary.image}
          rightText={primary.right_text?.richText}
          leftText={primary.left_text?.richText}
        />
      );
    case 'contact_form':
      return (
        <ContactFormSection
          title={primary.title?.text}
          description={primary.description?.richText}
          portalId={primary.portal_id}
          formId={primary.form_id}
        />
      );
    case 'promotions':
      return (
        <Promotions
          items={items}
          itemsPerRow={itemsPerRow}
          enclosedContainer={primary.enclosed_container}
          alignment={primary.alignment}
        />
      );
    case 'carousel':
      return <Carousel align={primary.align} items={items} />;
    case 'confirmation_message':
      return <ConfirmationMessage items={items} />;
    case 'resource_center_posts':
      return <ResourcePosts title={primary.title?.text} items={items} />;
    case 'enclosed_newsletter':
      return (
        <EnclosedNewsletterForm
          title={primary.title?.text}
          description={primary.description?.richText}
          confirmationMessage={primary.confirmation_message?.richText}
          confirmationTitle={primary.confirmation_title?.text}
          buttonLabel={primary.button_label}
          link={primary.link}
          linkLabel={primary.link_label}
          backgroundColor={primary.background_color || 'white'}
          formId={primary.form_id}
          embedCode={primary.embed_code}
        />
      );
    case 'cta_newsletter':
      return (
        <CTANewsletter
          title={primary.title?.text}
          description={primary.description?.richText}
          backgroundColor={primary.background_color || 'white'}
          formId={primary.form_id}
          portalId={primary.portal_id}
        />
      );
    case 'newsletter_form':
      return (
        <NewsletterFormSection
          title={primary.title?.text}
          description={primary.description?.richText}
          confirmationMessage={primary.confirmation_message?.richText}
          confirmationTitle={primary.confirmation_title?.text}
          buttonLabel={primary.button_label}
          legalText={primary.legal_text?.richText}
          backgroundColor={primary.background_color || 'white'}
        />
      );
    case 'comparison':
      return <Comparison tableContent={primary.table_content} />;
    case 'stat_blocks':
      return (
        <StatBlocks
          title={primary.title?.text}
          description={primary.description?.richText}
          displayBorder={primary.display_border}
          backgroundAlignment={primary.background_alignment}
          outerbackgroundColor={primary.outer_background_color}
          backgroundColor={primary.background_color}
          items={items}
        />
      );
    case 'feature_map':
      return (
        <FeatureMap title={{ text: primary.title?.text }} image={primary.image} items={items} />
      );
    case 'feature':
      return (
        <Feature
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          description={primary.description?.richText}
          image={primary.image}
          link={primary.link}
          blendImage={primary.blend_image}
          linkLabel={primary.link_label}
          backgroundColor={primary.background_color || 'white'}
          imageAlignment={primary.image_alignment || 'default'}
          items={items}
          imageFit={primary.image_fit}
          imageCrop={primary.image_crop || 'none'}
          embed={primary.embed}
        />
      );
    case 'feature_carousel':
      return (
        <FeatureCarousel
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          description={primary.description?.richText}
          link={primary.link}
          linkLabel={primary.link_label}
          backgroundColor={primary.background_color}
          items={items}
        />
      );
    case 'long_content':
      return (
        <LongContent
          image={primary.image}
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          description={primary.description?.richText}
          imageAlignment={primary.image_alignment}
          link={primary.link}
          linkLabel={primary.link_label}
          backgroundColor={primary.background_color}
        />
      );
    case 'video':
      return (
        <Video
          video_url={primary.video_url}
          title={primary.title?.text}
          subheading={primary.subheading?.richText}
          description={primary.description?.richText}
          link={primary.link}
          linkLabel={primary.link_label}
          sectionStyle={primary.section_style}
          backgroundColor={primary.background_color}
        />
      );
    case 'content_columns':
      return (
        <ContentColumns
          title={primary.title?.text}
          description={primary.description?.richText}
          twitter_icon={primary.twitter_icon}
          linkedin_icon={primary.linkedin_icon}
          facebook_icon={primary.facebook_icon}
          items={items}
        />
      );
    case 'quote':
      return (
        <Quote
          backgroundColor={primary.background_color}
          quote={primary.quote?.richText}
          authorName={primary.author_name}
          authorTitle={primary.author_title}
        />
      );
    case 'dual_list':
      return (
        <FeatureLists
          title={primary.title?.text}
          image={primary.image}
          imageAlignment={primary.image_alignment}
          imageFit={primary.image_fit}
          lists={primary.lists}
        />
      );
    case 'ticker':
      return (
        <Ticker
          description={primary.description?.richText || []}
          linkLabel={primary.link_label}
          link={primary.link}
        />
      );
    case 'notification_overlay':
      return (
        <NotificationOverlay
          image={primary.image}
          link={primary.link}
          linkLabel={primary.link_label}
          isDismissible={primary.is_notification_dismissible}
        />
      );
    case 'embed':
      return <EmbedSlice embedCode={primary.embed_code} title={primary.title?.text} />;
    case 'text_image_cta':
      return (
        <TextImageCta
          title={primary.title?.text}
          description={primary.description?.richText}
          link={primary.primary_link}
          linkLabel={primary.primary_link_title}
          secondaryLink={primary.secondary_link}
          secondaryLinkLabel={primary.secondary_link_title}
          image={primary.image}
          backgroundImage={primary.background_image}
        />
      );
    default:
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[renderModule] Unknown slice type: "${slice_type}". Add it to src/components/module/index.tsx and src/queries/page.ts`
        );
        return (
          <div
            style={{
              border: '2px dashed #aa1ff5',
              padding: '24px',
              margin: '16px 0',
              backgroundColor: '#faf5ff',
              fontFamily: 'monospace',
              borderRadius: '4px',
            }}
          >
            <strong style={{ color: '#aa1ff5' }}>
              ⚠️ Unregistered slice: <code>{slice_type}</code>
            </strong>
            <p style={{ margin: '8px 0 0', color: '#555', fontSize: '13px' }}>
              This slice exists in Prismic but is not yet wired in Gatsby.
              <br />
              Add a <code>case &apos;{slice_type}&apos;</code> in{' '}
              <code>src/components/module/index.tsx</code> and query its fields in{' '}
              <code>src/queries/page.ts</code>.
            </p>
          </div>
        );
      }
      return null;
  }
};

export default renderModule;
